import { useId, useState } from "react";

import { MuiMenuSelect, type MuiMenuSelectOption } from "@/components/ui/mui-menu-select";
import { MUI_SELECT_CARET_ANCHOR, MuiSelectCaret } from "@/components/ui/mui-select-caret";
import { OutlinedField } from "@/components/ui/outlined-field";
import { cn } from "@/lib/utils";

// A form's outlined MUI `Select` — react-ui's `SelectField` (`SelectField.tsx:
// 44-76`) over MUI's `TextField select`: an `OutlinedField` frame and floating
// label round the shared `MuiMenuSelect`, so what a keyboard or pointer reaches
// is MUI's `SelectInput` / `Menu` / `MenuList` (Tab closes the menu, a window
// blur or resize leaves it open, the arrows stop at the ends, MUI's typeahead
// buffers, a held opening press chooses on release), at MUI's default scroll
// lock, as a `Select`'s `Menu` has it.
//
// **Named as the donor names it**: `accessibleName` is `SelectField`'s
// `ariaLabel`, a hidden span read before the display itself.

export interface OutlinedMenuSelectFieldProps {
  label: string;
  /** `SelectField`'s `ariaLabel` — what the combobox is called, before its value. */
  accessibleName: string;
  required?: boolean;
  /** The chosen option's value, `""` for none. */
  value: string;
  options: MuiMenuSelectOption[];
  onValueChange: (value: string) => void;
  disabled?: boolean;
  /** `SelectField`'s rule message, under the field in the error colours. */
  error?: string;
}

export function OutlinedMenuSelectField({
  label,
  accessibleName,
  required = false,
  value,
  options,
  onValueChange,
  disabled = false,
  error,
}: OutlinedMenuSelectFieldProps) {
  const fieldId = useId();
  const nameId = useId();
  const labelId = useId();
  const [open, setOpen] = useState(false);
  // MUI's `FormControl` focus, which shrinks the label: on the field, and —
  // through the portal's React tree — on the menu it opened.
  const [focused, setFocused] = useState(false);

  return (
    <OutlinedField
      label={label}
      required={required}
      shrink={value !== "" || focused || open}
      disabled={disabled}
      focused={open}
      labelId={labelId}
      error={error}
    >
      <div
        className="contents"
        onFocus={() => setFocused(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget) && !open) setFocused(false);
        }}
      >
        <MuiMenuSelect
          id={fieldId}
          aria-labelledby={`${nameId} ${fieldId}`}
          required={required}
          disabled={disabled}
          value={value}
          options={options}
          onValueChange={onValueChange}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setFocused(document.activeElement?.id === fieldId);
          }}
          scrollLock
          anchorClassName={cn(MUI_SELECT_CARET_ANCHOR, "flex h-14 w-full items-center")}
          className={cn(
            // `.MuiSelect-select` in an outlined `InputBase`: 16.5px / 14px
            // padding round a 23px line, one line clipped with an ellipsis
            // (`SelectInput.js:92-100`) — 56px, the text where modsy draws it.
            "text-lennar-ink block h-14 w-full cursor-pointer truncate py-[16.5px] pr-8 pl-3.5 text-base leading-[23px] outline-none select-none",
            "aria-disabled:text-lennar-ink-disabled aria-disabled:cursor-default",
          )}
          caret={(isOpen) => (
            <MuiSelectCaret
              open={isOpen}
              // `.MuiSelect-icon.Mui-disabled` — the caret fades with its
              // field. `MUI_SELECT_CARET_DISABLED`'s ink, stated on the caret:
              // that constant keys off a `disabled` / `data-disabled` ancestor,
              // and MUI's select display is only ever `aria-disabled`.
              className={disabled ? "text-lennar-action-disabled opacity-100" : undefined}
            />
          )}
        />
      </div>
      <span id={nameId} className="sr-only">
        {accessibleName}
      </span>
    </OutlinedField>
  );
}
